import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileText, Printer, Building2, Eye } from "lucide-react";
import { useAuth } from "@/App";
import CoaForm from "./CoaForm";
import CoaPreview from "./CoaPreview";
import CompanyManager from "./CompanyManager";

const API = process.env.REACT_APP_BACKEND_URL;

const blankCoa = {
  company_id: "",
  company_name: "",
  license_no: "",
  document_title: "FINISHED PRODUCT CERTIFICATE OF ANALYSIS",
  department_text: "QUALITY CONTROL DEPARTMENT",
  act_text: "THE DRUG & COSMETIC ACT. 1940 & THE RULES THEREUNDER",
  footer_code: "",
  product_name: "",
  packing: "",
  generic_name: "",
  product_code: "",
  batch_no: "",
  actual_batch_size: "",
  sample_size: "",
  released_qty: "",
  remarks: "",
  mfg_date: "",
  exp_date: "",
  test_packing: "",
  test_as_per: "",
  ar_no: "",
  rel_dt: "",
  tr_slip_no: "",
  tr_slip_dt: "",
  analysis_date: "",
  specification_no: "",
  specification_dt: "",
  stp_no: "",
  location: "",
  make: "",
  tests: [{ test: "", result: "", specification: "" }],
  conclusion: "",
  prepared_by: "",
  prepared_role: "",
  checked_by: "",
  checked_role: "",
  approved_by: "",
  approved_role: "",
  save_as_template: true,
};

export default function CoaPanel() {
  const { token } = useAuth();
  const [view, setView] = useState("list"); // list | form | preview
  const [companies, setCompanies] = useState([]);
  const [coas, setCoas] = useState([]);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [current, setCurrent] = useState(null);
  const [companyMgrOpen, setCompanyMgrOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewCoa, setPreviewCoa] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchCompanies = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/documents/companies`, { headers });
      if (r.ok) {
        const d = await r.json();
        setCompanies(d.companies || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  const fetchCoas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (companyFilter) params.set("company_id", companyFilter);
      const r = await fetch(`${API}/api/documents/coa?${params}`, { headers });
      if (r.ok) {
        const d = await r.json();
        setCoas(d.certificates || []);
      }
    } finally {
      setLoading(false);
    }
  }, [token, search, companyFilter]);

  useEffect(() => { if (token) { fetchCompanies(); fetchCoas(); } }, [token, fetchCompanies, fetchCoas]);

  const handleNew = () => {
    setCurrent({ ...blankCoa });
    setView("form");
  };

  const handleEdit = (coa) => {
    setCurrent({
      ...blankCoa,
      ...coa,
      tests: coa.tests?.length ? coa.tests : [{ test: "", result: "", specification: "" }],
      save_as_template: true,
    });
    setView("form");
  };

  const handlePreview = (coa) => {
    setPreviewCoa(coa);
    setView("preview");
  };

  const handleDelete = async (coa) => {
    if (!window.confirm(`Delete COA for ${coa.product_name || coa.batch_no || coa.id}?`)) return;
    const r = await fetch(`${API}/api/documents/coa/${coa.id}`, { method: "DELETE", headers });
    if (r.ok) {
      toast.success("Deleted");
      fetchCoas();
    } else {
      toast.error("Delete failed");
    }
  };

  const handleSave = async (form) => {
    const isEdit = !!form.id;
    const url = isEdit ? `${API}/api/documents/coa/${form.id}` : `${API}/api/documents/coa`;
    const r = await fetch(url, {
      method: isEdit ? "PUT" : "POST",
      headers,
      body: JSON.stringify(form),
    });
    if (!r.ok) {
      const t = await r.text();
      toast.error("Save failed: " + t);
      return null;
    }
    const saved = await r.json();
    toast.success(isEdit ? "Updated" : "Saved");
    fetchCoas();
    setPreviewCoa(saved);
    setView("preview");
    return saved;
  };

  if (view === "form") {
    return (
      <CoaForm
        initial={current}
        companies={companies}
        onCancel={() => setView("list")}
        onSave={handleSave}
        onOpenCompanyMgr={() => setCompanyMgrOpen(true)}
        token={token}
      />
    );
  }

  if (view === "preview" && previewCoa) {
    return (
      <CoaPreview
        coa={previewCoa}
        onClose={() => setView("list")}
        onEdit={() => handleEdit(previewCoa)}
      />
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search batch no / product / AR No"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="coa-search-input"
              />
            </div>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="h-9 px-2 rounded-md border bg-background text-sm"
              data-testid="coa-company-filter"
            >
              <option value="">All companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setCompanyMgrOpen(true)} data-testid="coa-companies-btn">
              <Building2 className="w-4 h-4" /> Companies
            </Button>
            <Button size="sm" className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleNew} data-testid="coa-new-btn">
              <Plus className="w-4 h-4" /> New COA
            </Button>
          </div>

          {loading ? (
            <div className="text-center text-sm text-muted-foreground py-10">Loading…</div>
          ) : coas.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No certificates yet. Click <b>New COA</b> to create your first one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">Product</th>
                    <th className="px-3 py-2 font-medium">Batch</th>
                    <th className="px-3 py-2 font-medium hidden md:table-cell">AR No</th>
                    <th className="px-3 py-2 font-medium hidden lg:table-cell">Company</th>
                    <th className="px-3 py-2 font-medium hidden sm:table-cell">Created</th>
                    <th className="px-3 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coas.map((c) => (
                    <tr key={c.id} className="border-t hover:bg-muted/30" data-testid={`coa-row-${c.id}`}>
                      <td className="px-3 py-2 font-medium">{c.product_name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{c.batch_no || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs hidden md:table-cell">{c.ar_no || "—"}</td>
                      <td className="px-3 py-2 hidden lg:table-cell">
                        {c.company_name ? <Badge variant="outline">{c.company_name}</Badge> : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground hidden sm:table-cell">
                        {c.created_at ? String(c.created_at).slice(0, 10) : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handlePreview(c)} data-testid={`coa-preview-${c.id}`} title="Preview / Print">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(c)} data-testid={`coa-edit-${c.id}`} title="Edit">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700" onClick={() => handleDelete(c)} data-testid={`coa-delete-${c.id}`} title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={companyMgrOpen} onOpenChange={setCompanyMgrOpen}>
        <DialogContent mobileFullScreen className="max-w-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh] p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 sm:px-6 sm:pt-6">
            <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-indigo-500" /> Companies</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <CompanyManager
              companies={companies}
              onRefresh={fetchCompanies}
              token={token}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
